import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { apiErrorMessage } from '@/components/apiRequest';

import { reloadPage } from './browser';
import { createSessionSignals, registerSessionSignals } from './sessionSignals';

import type { SnackbarSetter } from '@/components/NewUI/ComponentsLibrary';

export const GENERIC_ERROR = 'Something went wrong. Please try again.';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// RecruitApiClient throws `{ ...payload, code }`; axios errors carry `response.status`.
export const errorStatus = (error: unknown): number | null => {
  if (!isRecord(error)) return null;
  if (typeof error.code === 'number') return error.code;
  if (typeof error.status === 'number') return error.status;
  if (isRecord(error.response) && typeof error.response.status === 'number') {
    return error.response.status;
  }
  return null;
};

export const errorMessage = (error: unknown, fallback: string = GENERIC_ERROR): string =>
  apiErrorMessage(error, fallback);

// Api::V4::AuthenticatedController: the tab's page belongs to a user this browser no longer holds.
export const isSessionUserChanged = (error: unknown): boolean =>
  errorStatus(error) === 409 && isRecord(error) && error.error === 'session_user_changed';

const unauthenticatedMessage = (error: unknown): string | null =>
  isRecord(error) && typeof error.error === 'string' && error.error !== '' ? error.error : null;

// A 4xx is the server's answer and asking again cannot change it (a 401 ends the session). A
// network failure or a 5xx may pass: two more tries, then the error stands. A query's own
// `retry` wins; mutations keep TanStack's default of none.
const MAX_QUERY_RETRIES = 2;

export const retryShellQuery = (failureCount: number, error: unknown): boolean => {
  const status = errorStatus(error);
  if (status !== null && status >= 400 && status < 500) return false;
  return failureCount < MAX_QUERY_RETRIES;
};

const skipGlobalSnackbar = (meta: Record<string, unknown> | undefined): boolean =>
  meta?.skipGlobalErrorSnackbar === true;

// The page's own wording for a failure the server did not explain ("Menu item could not be
// saved."), so a page needs no snackbar host of its own to keep it.
const errorFallbackOf = (meta: Record<string, unknown> | undefined): string | undefined =>
  typeof meta?.errorFallback === 'string' && meta.errorFallback !== ''
    ? meta.errorFallback
    : undefined;

interface ShellQueryClientOptions {
  notify: SnackbarSetter;
  reload?: () => void;
}

// The shell's one client. A 401 means the session ended, and a 409 session_user_changed that
// another tab signed in as someone else: the shell's session machine takes both when it runs
// (`shell_session_push`), and otherwise the page reloads so Rails redirects to login and
// `set_return_to_location` brings the user back. Every other failure goes to the shell snackbar,
// in the server's words or the operation's `meta.errorFallback`, unless the operation opts out
// with `meta.skipGlobalErrorSnackbar` and shows the error inline.
export const createShellQueryClient = ({
  notify,
  reload = reloadPage
}: ShellQueryClientOptions): QueryClient => {
  const session = createSessionSignals();
  const handle = (error: unknown, meta: Record<string, unknown> | undefined) => {
    if (errorStatus(error) === 401) {
      if (!session.emit({ type: 'unauthenticated', message: unauthenticatedMessage(error) })) {
        reload();
      }
      return;
    }
    if (isSessionUserChanged(error)) {
      if (!session.emit({ type: 'userChanged' })) reload();
      return;
    }
    if (skipGlobalSnackbar(meta)) return;
    notify({ message: errorMessage(error, errorFallbackOf(meta)), state: 'error' });
  };

  const client = new QueryClient({
    queryCache: new QueryCache({ onError: (error, query) => handle(error, query.meta) }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => handle(error, mutation.meta)
    }),
    defaultOptions: { queries: { refetchOnWindowFocus: false, retry: retryShellQuery } }
  });
  registerSessionSignals(client, session);
  return client;
};
